/* Diamond Studio — payment trust signals (2026-09-29).
   Uses the store's enabled payment methods (see snippets/ds-pay-trust.liquid) to show:
   - product page: "Secure, encrypted checkout" + payment icons under the buy buttons
   - product page: a "Secure payment" row in "Confidence in the details"
   - cart drawer: the same secure line + icons under the checkout button
   It also refreshes older wording that said only PayPal was accepted
   (written before Shopify Payments was active). */
(() => {
  'use strict';
  if (window.DSPayTrust) return;
  const dataEl = document.getElementById('ds-pay-trust-data');
  const tpl = document.getElementById('ds-pay-trust-icons');
  if (!dataEl || !tpl) return;
  let types = [];
  try { types = JSON.parse(dataEl.textContent).types || []; } catch (e) { return; }
  if (!types.length) return;

  const CARDS = ['visa', 'master', 'american_express', 'discover', 'diners_club', 'jcb', 'unionpay', 'elo', 'maestro'];
  const NAMES = {paypal: 'PayPal', apple_pay: 'Apple Pay', google_pay: 'Google Pay', shopify_pay: 'Shop Pay', amazon_pay: 'Amazon Pay', venmo: 'Venmo', klarna: 'Klarna', afterpay: 'Afterpay'};
  // Display order: cards first, then PayPal and the wallets.
  const ORDER = ['visa', 'master', 'american_express', 'discover', 'diners_club', 'jcb', 'unionpay', 'elo', 'maestro', 'paypal', 'venmo', 'shopify_pay', 'apple_pay', 'google_pay', 'amazon_pay', 'klarna', 'afterpay'];
  const rank = t => { const i = ORDER.indexOf(t); return i < 0 ? ORDER.length : i; };
  types = types.slice().sort((a, b) => rank(a) - rank(b));
  // Affirm financing is only offered to U.S. shoppers paying in USD (same rule as the theme's Affirm messaging).
  const us = () => window.DS_CRO?.country === 'US' && window.DS_CRO?.currency === 'USD';
  // Venmo (through PayPal) only works for U.S. shoppers; Affirm is U.S.-only too.
  const US_ONLY = ['venmo', 'affirm'];
  const visibleTypes = () => us() ? types : types.filter(t => !US_ONLY.includes(t));

  function methods(withAffirm) {
    const list = [];
    const shown = visibleTypes();
    if (shown.some(t => CARDS.includes(t))) list.push('card');
    shown.forEach(t => { if (NAMES[t] && !list.includes(NAMES[t])) list.push(NAMES[t]); });
    if (withAffirm && us()) list.push('Affirm');
    return list;
  }
  const join = list => list.length < 2 ? (list[0] || '') : list.slice(0, -1).join(', ') + ' or ' + list[list.length - 1];
  const cap = s => s.charAt(0).toUpperCase() + s.slice(1);
  const sentence = () => 'Pay by ' + join(methods(false)) + '.';

  function icons() {
    const list = tpl.content.firstElementChild.cloneNode(true);
    const shown = visibleTypes();
    const items = [...list.children].filter(li => shown.includes(li.dataset.type));
    items.sort((a, b) => rank(a.dataset.type) - rank(b.dataset.type));
    list.replaceChildren(...items);
    return list;
  }
  const LOCK = '<svg class="ds-pt-lock" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><rect x="5" y="10.5" width="14" height="10" rx="2"/><path d="M8.5 10.5V7.5a3.5 3.5 0 0 1 7 0v3"/><path d="M12 14.5v2.5"/></svg>';

  function block(modifier) {
    const box = document.createElement('div');
    box.className = 'ds-pt ds-pt--' + modifier;
    box.dataset.dsPayTrust = modifier;
    box.innerHTML = '<p class="ds-pt__secure">' + LOCK + '<span>Secure, encrypted checkout</span></p>';
    box.append(icons());
    return box;
  }

  const track = (event, placement) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({event, placement});
  };
  const openPaymentOptions = () => document.querySelector('[data-pdp-action="payments"]')?.click();

  /* ---------- Product page ---------- */
  function placePdp() {
    const buy = document.getElementById('buy-actions');
    if (buy && !document.querySelector('[data-ds-pay-trust="pdp"]')) buy.after(block('pdp'));
    placeCareRow();
    fixFallback();
  }

  // "Confidence in the details" list: add a "Secure payment" row that opens Payment options.
  function placeCareRow() {
    const nav = document.querySelector('#care .ds-purchase-policy-links');
    if (!nav || nav.querySelector('[data-ds-pt-care]')) return;
    const model = nav.querySelector('a');
    if (!model || !document.querySelector('[data-pdp-action="payments"]')) return;
    const row = model.cloneNode(true);
    row.setAttribute('href', '#payment-options');
    row.setAttribute('role', 'button');
    row.setAttribute('aria-haspopup', 'dialog');
    row.removeAttribute('aria-controls');
    row.dataset.dsPtCare = '';
    const art = row.querySelector('.ds-policy-icon svg');
    if (art) art.innerHTML = '<rect x="8" y="24" width="60" height="40" rx="4" fill="var(--icon-metal, #eef0ec)"/><path d="M8 35H68"/><rect x="16" y="43" width="12" height="9" rx="1.5" fill="var(--icon-face, #fffefa)"/><path d="M16 57H38"/><path d="M62 54V47a9 9 0 0 1 18 0v7"/><rect x="56" y="54" width="30" height="24" rx="3" fill="var(--icon-face, #fffefa)"/><circle cx="71" cy="64" r="2.5" fill="currentColor" stroke="none"/><path d="M71 66v5"/>';
    const title = row.querySelector('.ds-policy-copy strong');
    const text = row.querySelector('.ds-policy-copy small');
    if (title) title.textContent = 'Secure payment';
    if (text) text.textContent = cap(join(methods(true))) + '. Every payment is encrypted.';
    row.addEventListener('click', event => {
      event.preventDefault();
      track('ds_payment_trust_open', 'pdp_care');
      openPaymentOptions();
    });
    // Order: Shipping, Returns, Secure payment, Talk to the studio.
    const links = nav.querySelectorAll('a');
    if (links[2]) links[2].before(row);
    else nav.append(row);
  }

  // Under-price note for shoppers outside the U.S. used to say "PayPal accepted at checkout."
  function fixFallback() {
    if (us()) return;
    document.querySelectorAll('.ds-affirm-fallback').forEach(p => {
      if (!p.dataset.dsPt) { p.textContent = sentence(); p.dataset.dsPt = '1'; }
    });
  }

  // "Payment options" window. U.S.: Diamond Studio's Affirm sheet gets a "Pay in full" part added.
  // Everyone else: the old "PayPal only" text is replaced with the real list of methods.
  function patchPreviewPayments() {
    if (window.DS_PREVIEW_PAYMENTS && !window.DS_PREVIEW_PAYMENTS.dsPt) {
      const replacement = ({show}) => {
        track('ds_payment_trust_open', 'pdp_payment_options');
        if (window.DSPaymentMessaging?.open?.(document.activeElement) === true) return;
        show('Payment options.', '<div class="ds-pt-dialog"><p>' + sentence() + ' Every payment is encrypted at checkout.</p>' + icons().outerHTML + '<p>Available payment methods are confirmed at checkout for your destination.</p></div>');
      };
      replacement.dsPt = true;
      window.DS_PREVIEW_PAYMENTS = replacement;
    }
  }
  function patchAffirmSheet() {
    const sheet = document.querySelector('#ds-payment-info-dialog .ds-payment-sheet');
    if (!sheet || sheet.querySelector('[data-ds-pt-sheet]')) return;
    const intro = sheet.querySelector('.ds-payment-sheet__intro');
    if (intro) intro.textContent = 'Pay in full at checkout, or spread the cost over time with Affirm.';
    const full = document.createElement('div');
    full.className = 'ds-pt-sheet';
    full.dataset.dsPtSheet = '';
    full.innerHTML = '<p class="ds-pt-sheet__title">Pay in full</p><p>' + sentence() + ' Every payment is encrypted.</p>';
    full.append(icons());
    const next = document.createElement('p');
    next.className = 'ds-pt-sheet__title ds-pt-sheet__next';
    next.textContent = 'Or pay over time with Affirm';
    const steps = sheet.querySelector('ol');
    if (steps) { steps.before(full); steps.before(next); } else if (intro) intro.after(full);
  }

  /* ---------- Collection pages: payment note above the grid ---------- */
  // U.S.: "Pay over time with Affirm … PayPal also accepted." → lists every method.
  // Elsewhere: "PayPal accepted." → the real list of methods + payment icons.
  function patchCollectionFinancing() {
    document.querySelectorAll('.ds-studio-financing').forEach(box => {
      if (box.dataset.dsPt) return;
      box.dataset.dsPt = '1';
      const also = box.querySelector('.ds-studio-paypal');
      if (box.dataset.studioAffirm === 'true' && also) {
        also.textContent = cap(join(methods(false)).replace(/ or /, ' and ')) + ' also accepted.';
        return;
      }
      const title = box.querySelector(':scope > strong');
      if (!title || box.dataset.studioAffirm === 'true') return;
      title.textContent = sentence();
      const list = icons();
      list.classList.add('ds-pt-icons--inline');
      title.after(list);
    });
  }

  /* ---------- Older "PayPal only" wording anywhere else on the page ---------- */
  // e.g. the "Found something you love? PayPal accepted at checkout." card in the collection grid,
  // and the note inside the collection "Make an offer" window.
  const STALE = ['PayPal accepted at checkout.', 'PayPal accepted.', 'Pay securely with PayPal at checkout.'];
  const ALSO = 'PayPal also accepted.';
  const patched = new WeakSet();
  function patchStaleText(root) {
    const scope = root || document.body;
    if (!scope || !(scope.textContent || '').includes('PayPal')) return;
    const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
    const hits = [];
    while (walker.nextNode()) {
      const node = walker.currentNode;
      if (patched.has(node)) continue;
      const text = node.nodeValue.trim();
      if (STALE.includes(text) || text.includes(ALSO)) hits.push(node);
    }
    hits.forEach(node => {
      const text = node.nodeValue.trim();
      node.nodeValue = text.includes(ALSO)
        ? node.nodeValue.replace(ALSO, cap(join(methods(false)).replace(/ or /, ' and ')) + ' also accepted.')
        : node.nodeValue.replace(text, sentence());
      patched.add(node);
    });
    document.querySelectorAll('.ds-payment-card--paypal .ds-payment-card__copy').forEach(copy => {
      if (copy.querySelector('.ds-pt-icons')) return;
      const list = icons();
      list.classList.add('ds-pt-icons--inline');
      copy.append(list);
    });
  }

  /* ---------- Cart drawer ---------- */
  function placeCart() {
    const footer = document.querySelector('#ds-cart-drawer .ds-bag-footer');
    const checkout = footer?.querySelector('[name="checkout"]');
    if (!checkout) return;
    // Outside the U.S. the drawer said "Pay securely with PayPal at checkout." — the icons below replace it.
    if (!us()) footer.querySelectorAll('[data-ds-payment-cart]').forEach(note => note.remove());
    if (!footer.querySelector('[data-ds-pay-trust="cart"]')) checkout.after(block('cart'));
  }

  function run() {
    placePdp();
    placeCart();
    patchCollectionFinancing();
    patchStaleText();
    patchPreviewPayments();
    patchAffirmSheet();
  }

  document.addEventListener('ds:bag-rendered', placeCart);
  // Dialogs and "load more" add content later: fix any old wording as it appears.
  new MutationObserver(records => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node.nodeType === 1 && (node.textContent || '').includes('PayPal')) patchStaleText(node);
        else if (node.nodeType === 3 && node.parentNode && (node.nodeValue || '').includes('PayPal')) patchStaleText(node.parentNode);
      }
    }
  }).observe(document.documentElement, {childList: true, subtree: true});
  document.addEventListener('click', event => {
    if (event.target.closest?.('[data-pdp-action="payments"], [data-ds-payment-info]')) {
      patchPreviewPayments();
      patchAffirmSheet();
      // The Affirm sheet may be created by this click; patch it once it exists.
      setTimeout(patchAffirmSheet, 0);
    }
  }, true);

  // The product page builds its purchase area after load; watch briefly for it.
  let observer;
  const start = () => {
    run();
    observer = new MutationObserver(() => { observer.disconnect(); run(); observe(); });
    const observe = () => observer.observe(document.body, {childList: true, subtree: true});
    observe();
    setTimeout(() => observer.disconnect(), 15000);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
  window.addEventListener('load', run, {once: true});

  window.DSPayTrust = {types, methods, sentence, icons};
})();
